import { useState, useCallback } from 'react'

export interface ValidationRule<T = unknown> {
  required?: boolean
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  pattern?: RegExp
  custom?: (value: T) => string | null
  message?: string
}

export interface ValidationRules {
  [field: string]: ValidationRule
}

export interface ValidationErrors {
  [field: string]: string
}

export interface FormValidationResult {
  isValid: boolean
  errors: ValidationErrors
  hasError: (field: string) => boolean
  getError: (field: string) => string | null
  validateField: (field: string, value: unknown) => string | null
  validateForm: (data: Record<string, unknown>) => boolean
  clearError: (field: string) => void
  clearAllErrors: () => void
  setError: (field: string, message: string) => void
}

export function useFormValidation(rules: ValidationRules): FormValidationResult {
  const [errors, setErrors] = useState<ValidationErrors>({})

  const validateField = useCallback((field: string, value: unknown): string | null => {
    const rule = rules[field]
    if (!rule) return null

    // Required validation
    if (rule.required && (value === null || value === undefined || value === '')) {
      return rule.message || `${field} is required`
    }

    // Skip other validations if value is empty and not required
    if (!rule.required && (value === null || value === undefined || value === '')) {
      return null
    }

    // String validations
    if (typeof value === 'string') {
      if (rule.minLength && value.length < rule.minLength) {
        return rule.message || `${field} must be at least ${rule.minLength} characters`
      }
      if (rule.maxLength && value.length > rule.maxLength) {
        return rule.message || `${field} must be no more than ${rule.maxLength} characters`
      }
      if (rule.pattern && !rule.pattern.test(value)) {
        return rule.message || `${field} format is invalid`
      }
    }

    // Number validations
    if (typeof value === 'number') {
      if (rule.min !== undefined && value < rule.min) {
        return rule.message || `${field} must be at least ${rule.min}`
      }
      if (rule.max !== undefined && value > rule.max) {
        return rule.message || `${field} must be no more than ${rule.max}`
      }
    }

    // Custom validation
    if (rule.custom) {
      const customError = rule.custom(value)
      if (customError) {
        return customError
      }
    }

    return null
  }, [rules])

  const validateForm = useCallback((data: Record<string, unknown>): boolean => {
    const newErrors: ValidationErrors = {}
    let isValid = true

    Object.keys(rules).forEach(field => {
      const rule = rules[field]
      if (!rule) return

      const value = data[field]
      let error: string | null = null

      // Required validation
      if (rule.required && (value === null || value === undefined || value === '')) {
        error = rule.message || `${field} is required`
      }
      // Skip other validations if value is empty and not required
      else if (!rule.required && (value === null || value === undefined || value === '')) {
        error = null
      }
      // String validations
      else if (typeof value === 'string') {
        if (rule.minLength && value.length < rule.minLength) {
          error = rule.message || `${field} must be at least ${rule.minLength} characters`
        } else if (rule.maxLength && value.length > rule.maxLength) {
          error = rule.message || `${field} must be no more than ${rule.maxLength} characters`
        } else if (rule.pattern && !rule.pattern.test(value)) {
          error = rule.message || `${field} format is invalid`
        }
      }
      // Number validations
      else if (typeof value === 'number') {
        if (rule.min !== undefined && value < rule.min) {
          error = rule.message || `${field} must be at least ${rule.min}`
        } else if (rule.max !== undefined && value > rule.max) {
          error = rule.message || `${field} must be no more than ${rule.max}`
        }
      }

      // Custom validation
      if (!error && rule.custom) {
        error = rule.custom(value)
      }

      if (error) {
        newErrors[field] = error
        isValid = false
      }
    })

    setErrors(newErrors)
    return isValid
  }, [rules])

  const hasError = useCallback((field: string): boolean => {
    return !!errors[field]
  }, [errors])

  const getError = useCallback((field: string): string | null => {
    return errors[field] || null
  }, [errors])

  const clearError = useCallback((field: string) => {
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }, [])

  const clearAllErrors = useCallback(() => {
    setErrors({})
  }, [])

  const setError = useCallback((field: string, message: string) => {
    setErrors(prev => ({ ...prev, [field]: message }))
  }, [])

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    hasError,
    getError,
    validateField,
    validateForm,
    clearError,
    clearAllErrors,
    setError
  }
}

// Helper function to combine validation rules
export const combineRules = (...rules: ValidationRule[]): ValidationRule => {
  return rules.reduce((combined, rule) => ({ ...combined, ...rule }), {})
}

// Common validation rules
export const commonRules = {
  required: (message?: string): ValidationRule => ({
    required: true,
    message
  }),
  
  minLength: (length: number, message?: string): ValidationRule => ({
    minLength: length,
    message
  }),
  
  maxLength: (length: number, message?: string): ValidationRule => ({
    maxLength: length,
    message
  }),
  
  email: (message?: string): ValidationRule => ({
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: message || 'Invalid email format'
  }),
  
  number: (message?: string): ValidationRule => ({
    custom: (value) => {
      const num = parseFloat(value as string)
      return isNaN(num) ? (message || 'Must be a valid number') : null
    }
  }),
  
  positiveNumber: (message?: string): ValidationRule => ({
    custom: (value) => {
      const num = parseFloat(value as string)
      if (isNaN(num)) return message || 'Must be a valid number'
      if (num <= 0) return message || 'Must be a positive number'
      return null
    }
  }),
  
  nonNegativeNumber: (message?: string): ValidationRule => ({
    custom: (value) => {
      const num = parseFloat(value as string)
      if (isNaN(num)) return message || 'Must be a valid number'
      if (num < 0) return message || 'Cannot be negative'
      return null
    }
  }),
  
  accountType: (message?: string): ValidationRule => ({
    custom: (value) => {
      return (value === 'asset' || value === 'liability') 
        ? null 
        : (message || 'Must be either "asset" or "liability"')
    }
  })
}